/* global document */
import { queryDevicePropertyHistory, queryDevicePropertyAggregation, getChartDevice } from '@/api/device-chart'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'
import { deviceChartQuery, responseBody, defaultChartData, transformDeviceChart, validateChartData, historyRealtimeRows, mergeRealtimeRows } from './device-chart-data'
import { followsDeviceChart, liveRange, inLiveRange, propertySample, measurementPropertySample, bodyWithRows, liveAggregationQuery, createLiveAggregation } from './device-chart-live'
import { isStaticDataPreview, sharedReadOptions, readonlyDeviceStream } from './shared-view'
import { pageDevicePending, pageDeviceSkeleton } from './page-bound-placeholder'
import { pageDevicePropertyStream, usesPageDeviceProperties } from './page-device-properties'

const states = new WeakMap()
const names = ['avue-echart-line', 'avue-echart-bar']
const ownChart = vm => names.includes(vm.$options.name)

function stateOf (vm) {
  if (!states.has(vm)) states.set(vm, { sequence: 0, status: 'loading', error: '' })
  return states.get(vm)
}

export function chartHost (vm) {
  for (let parent = vm.$parent; parent; parent = parent.$parent) {
    if (parent.contain) return parent.contain
    if (parent.navigationContext) return parent
  }
  return {}
}

function bindingKey (vm) {
  const host = chartHost(vm), context = host.navigationContext || {}
  return JSON.stringify([vm.dataType, vm.parameter, context.deviceId, context.productId,
    !!host.menuFlag, !!host.isPreview, !!host.isSharedDataView, isStaticDataPreview(host), vm.time, vm.dataFormatter && vm.dataFormatter.toString()])
}

function showState (vm) {
  const state = stateOf(vm)
  if (!vm.$el || typeof document === 'undefined') return
  if (state.overlay && state.overlay.parentNode) state.overlay.parentNode.removeChild(state.overlay)
  state.overlay = null
  vm.$el.setAttribute('aria-busy', String(state.status === 'loading'))
  if (state.status === 'ready' || pageDeviceSkeleton(vm.parameter, state.status)) return
  const overlay = document.createElement('div')
  overlay.className = 'device-chart-feedback'
  overlay.setAttribute('role', state.status === 'error' ? 'alert' : 'status')
  const label = document.createElement('span')
  label.textContent = state.status === 'loading' ? '正在查询设备属性…'
    : state.status === 'empty' ? '当前范围暂无属性数据' : state.error
  overlay.appendChild(label)
  if (state.status === 'error') {
    const retry = document.createElement('button')
    retry.type = 'button'
    retry.textContent = '重试'
    retry.setAttribute('data-device-chart-retry', '')
    retry.onclick = () => vm.updateData()
    overlay.appendChild(retry)
  }
  vm.$el.appendChild(overlay)
  state.overlay = overlay
}

function renderData (vm, data, reset = false) {
  vm.dataChart = data
  if (vm.myChart) {
    // 新设备/配置才清空；WS 追加沿用原 ECharts 实例，不反复清空画布。
    if (reset) vm.myChart.clear()
    vm.updateChart()
    // 数据刷新不能重复注册点击事件。
    vm.myChart.off('click')
    if (vm.bindClick) vm.bindClick()
  }
}

function pendingChart (vm, host) {
  if (!pageDevicePending(vm.parameter, host.navigationContext || {}, !!host.menuFlag)) return false
  const state = stateOf(vm), property = vm.parameter && vm.parameter.property
  // 属性缺配置仍提示修正；只把缺少页面设备参数视为中性状态。
  if (property === undefined || property === null || property === '') {
    state.status = 'error'; state.error = '请选择设备属性'
  } else {
    state.status = 'pending'; state.error = ''
  }
  renderData(vm, { categories: [], series: [] }, true)
  showState(vm)
  return true
}

function unsubscribeChart (state) {
  const subscription = state.subscription
  state.subscription = null
  // 旧 Avue helper 在 socket 已关闭时取消订阅可能抛异常，不能阻断销毁/切页。
  if (subscription) { try { subscription.unsubscribe() } catch (error) { /* 已断开的 socket 无需再发送 unsub */ } }
}

export function stopDeviceChart (vm) {
  const state = stateOf(vm)
  state.sequence++
  clearTimeout(state.timer)
  clearTimeout(state.paintTimer)
  clearTimeout(state.correctionTimer)
  state.paintTimer = null
  state.correctionTimer = null
  clearInterval(vm.checkChart)
  if (state.cancellation) state.cancellation.cancel('设备或配置已切换')
  unsubscribeChart(state)
  state.cancellation = null
  state.subscription = null
  if (state.overlay && state.overlay.parentNode) state.overlay.parentNode.removeChild(state.overlay)
  state.overlay = null
  if (vm.$el && vm.$el.removeAttribute) vm.$el.removeAttribute('aria-busy')
}

function acceptData (vm, data) {
  const state = stateOf(vm)
  renderData(vm, data)
  state.status = data.categories.length ? 'ready' : 'empty'
  state.error = ''
  showState(vm)
}

async function subscribeRealtime (vm, query, options, current) {
  const state = stateOf(vm)
  // 仅做一次有上限的 HTTP 历史查询；独立通道 history=0，共享通道的最新首值参与去重合并。
  // 初始化期间先缓冲推送，HTTP 返回后合并，避免慢请求把新点覆盖掉。
  const host = chartHost(vm)
  let stream
  if (usesPageDeviceProperties(vm.parameter, host, query.deviceId)) stream = pageDevicePropertyStream(host, query.deviceId, query.property)
  else {
    let productId = vm.parameter.productId
    if (vm.parameter.deviceSource === 'page' || !productId) {
      const body = responseBody(await getChartDevice(query.deviceId, options))
      if (!current()) return
      productId = body.result && body.result.productId
    }
    if (!productId) throw new Error('设备没有产品信息，无法订阅实时属性')
    stream = readonlyDeviceStream(host, `device-chart-${vm._uid}-${Date.now()}-${state.sequence}`,
      `/dashboard/device/${productId}/${query.property}/realTime`, { deviceId: query.deviceId, history: 0 })
  }
  query = deviceChartQuery(vm.parameter, host.navigationContext || {}, !!host.menuFlag)
  const range = liveRange(query, vm.parameter)
  let rows = [], ready = false, streamError, recovering = false, recoveryPending = false, recoveryBuffer = []
  const publish = () => {
    try {
      // 实时自定义处理继续获得原来的 MeasurementValue payload 数组。
      const data = !host.isPreview && typeof vm.dataFormatter === 'function'
        ? validateChartData(vm.dataFormatter(rows.slice())) : defaultChartData(rows, vm.parameter)
      acceptData(vm, data)
    } catch (error) {
      state.status = 'error'; state.error = error.message || '实时数据处理失败'; showState(vm)
    }
  }
  const recover = async () => {
    if (!current()) return
    if (!ready || recovering) { recoveryPending = true; return }
    recoveryPending = false; recovering = true; recoveryBuffer = []
    try {
      const fresh = deviceChartQuery(vm.parameter, host.navigationContext || {}, !!host.menuFlag)
      const last = rows.reduce((value, row) => Math.max(value, Number(row.timestamp) || 0), fresh.range.from)
      const from = Math.max(fresh.range.from, Math.min(last, fresh.range.to) - 30000)
      if (from < fresh.range.to) {
        const data = { ...fresh.data, terms: [{ column: 'timestamp', termType: 'gte', value: from }, { column: 'timestamp', termType: 'lte', value: fresh.range.to }] }
        const body = responseBody(await queryDevicePropertyHistory(query.deviceId, query.property, data, options))
        if (!current()) return
        rows = mergeRealtimeRows(mergeRealtimeRows(rows, historyRealtimeRows(body, vm.parameter), query.limit), recoveryBuffer, query.limit).filter(row => inLiveRange(Number(row.timestamp), range))
        publish()
      }
    } catch (error) {
      if (current() && !isRequestCanceled(error)) { state.status = 'error'; state.error = error.message || '实时缺口回补失败，请重试'; showState(vm) }
    } finally { recovering = false; recoveryBuffer = []; if (recoveryPending && current()) recover() }
  }
  if (stream.onReconnect) stream.onReconnect(recover)
  state.subscription = stream.subscribe(response => {
    if (!current()) return
    const payload = response && response.payload
    if (!payload || typeof payload !== 'object') return
    if (!inLiveRange(Number(payload.timestamp), range)) return
    if (recovering) { recoveryBuffer.push(payload); if (recoveryBuffer.length > Math.max(200, query.limit * 4)) recoveryBuffer.shift() }
    rows = mergeRealtimeRows(rows, [payload], query.limit)
    if (ready) publish()
  }, error => {
    streamError = error || new Error('实时订阅失败，请重试')
    if (current()) { state.status = 'error'; state.error = streamError.message || '实时订阅失败，请重试'; showState(vm) }
  })
  if (state.subscription.ready) await state.subscription.ready
  if (!current()) return
  const initial = queryDevicePropertyHistory(query.deviceId, query.property, query.data, options)
    .then(response => ({ body: responseBody(response) })).catch(error => ({ error }))
  const result = await initial
  if (!current()) return
  if (result.error) throw result.error
  rows = mergeRealtimeRows(historyRealtimeRows(result.body, vm.parameter), rows, query.limit)
    .filter(row => inLiveRange(Number(row.timestamp), range))
  ready = true
  publish()
  if (streamError) throw streamError
  if (recoveryPending) recover()
}

async function subscribeHistory (vm, sourceQuery, options, current) {
  const state = stateOf(vm), host = chartHost(vm), aggregate = sourceQuery.mode === 1
  let query = aggregate ? liveAggregationQuery(sourceQuery) : sourceQuery
  let range = liveRange(query, vm.parameter), accumulator = aggregate ? createLiveAggregation(query, vm.parameter) : null
  let body, rows = [], ready = false, correcting = false, buffer = [], streamError, halted = false, recoveryPending = false
  const pending = new Map()
  const fail = error => {
    if (!current()) return
    halted = true
    clearTimeout(state.paintTimer); clearTimeout(state.correctionTimer)
    state.paintTimer = null; state.correctionTimer = null
    unsubscribeChart(state)
    if (state.cancellation) state.cancellation.cancel('实时接续已停止')
    state.status = 'error'; state.error = error.message || '实时接续失败，请重试'; showState(vm)
  }
  const publish = () => {
    if (!current() || halted) return
    try {
      acceptData(vm, transformDeviceChart(bodyWithRows(body, aggregate ? accumulator.rows() : rows), vm.parameter, vm.dataFormatter, !!host.isPreview))
    } catch (error) { fail(error) }
  }
  const paint = () => {
    if (state.paintTimer) return
    // 高频上报合并绘制，不为每个样本重绘整张 ECharts。
    state.paintTimer = setTimeout(() => { state.paintTimer = null; publish() }, 100)
  }
  const apply = sample => {
    if (aggregate) {
      const result = accumulator.append(sample)
      if (result === 'reconcile') {
        const key = accumulator.correctionKey(sample)
        pending.set(key, sample)
        if (pending.size > 32) throw new Error('待校准的聚合桶过多，请重试以回补历史')
        scheduleCorrection()
      } else if (result === 'changed') paint()
    } else {
      rows = mergeRealtimeRows(rows, [sample], query.limit)
      paint()
    }
  }
  const scheduleCorrection = () => {
    if (correcting || state.correctionTimer || !current() || halted) return
    state.correctionTimer = setTimeout(async () => {
      state.correctionTimer = null
      if (!current() || !pending.size) return
      const key = pending.keys().next().value, sample = pending.get(key)
      pending.delete(key)
      const data = accumulator.correction(sample)
      correcting = true
      try {
        const response = await queryDevicePropertyAggregation(query.deviceId, data, options)
        if (!current()) return
        const calibrated = accumulator.seed(responseBody(response), data.query.to, sample.timestamp)
        // 请求进行时缓冲消息；覆盖区间内由服务端结果校准，之后的新点本地追加。
        const waiting = buffer; buffer = []
        waiting.filter(item => item.timestamp < calibrated.from || item.timestamp > calibrated.to).forEach(apply)
        publish()
      } catch (error) {
        if (current() && !isRequestCanceled(error)) { fail(error); buffer = [] }
      } finally { correcting = false; if (current()) { if (recoveryPending) recover(); else scheduleCorrection() } }
    }, 800)
  }
  const shared = usesPageDeviceProperties(vm.parameter, host, query.deviceId)
  let stream
  if (shared) {
    // 历史已由各图表的有界查询初始化，不回放其他组件之前缓存的旧首值。
    stream = pageDevicePropertyStream(host, query.deviceId, query.property, { replay: false })
  } else {
    let productId = vm.parameter.productId
    if (vm.parameter.deviceSource === 'page' || !productId) {
      const detail = responseBody(await getChartDevice(query.deviceId, options))
      if (!current()) return
      productId = detail.result && detail.result.productId
    }
    if (!productId) throw new Error('设备没有产品信息，无法订阅实时属性')
    stream = readonlyDeviceStream(host, `device-chart-history-${vm._uid}-${Date.now()}-${state.sequence}`,
      `/device/${productId}/${query.deviceId}/message/property/**`, {})
  }
  const fresh = deviceChartQuery(vm.parameter, host.navigationContext || {}, !!host.menuFlag)
  query = aggregate ? liveAggregationQuery(fresh) : fresh
  range = liveRange(query, vm.parameter)
  accumulator = aggregate ? createLiveAggregation(query, vm.parameter) : null
  // 固定设备保留原始消息；页面整机通道同样携带设备属性采样时间，而非本机收包时间。
  const recover = async () => {
    if (!current() || halted) return
    if (!ready || correcting) { recoveryPending = true; return }
    correcting = true; recoveryPending = false
    clearTimeout(state.correctionTimer); state.correctionTimer = null
    try {
      const fresh = deviceChartQuery(vm.parameter, host.navigationContext || {}, !!host.menuFlag)
      const previous = aggregate ? accumulator.rows() : rows
      const last = previous.reduce((value, row) => Math.max(value, Number(row.timestamp) || 0), fresh.range.from)
      const from = Math.max(fresh.range.from, aggregate ? last : Math.min(last, fresh.range.to) - 30000), to = fresh.range.to
      if (from < to) {
        const data = aggregate ? { ...query.data, query: { ...query.data.query, from, to } }
          : { ...fresh.data, terms: [{ column: 'timestamp', termType: 'gte', value: from }, { column: 'timestamp', termType: 'lte', value: to }] }
        const response = aggregate ? await queryDevicePropertyAggregation(query.deviceId, data, options) : await queryDevicePropertyHistory(query.deviceId, query.property, data, options)
        if (!current() || halted) return
        const recovered = responseBody(response)
        if (aggregate) {
          accumulator.seed(recovered, to)
          pending.forEach((sample, key) => { if (sample.timestamp >= from && sample.timestamp <= to) pending.delete(key) })
        } else rows = mergeRealtimeRows(rows, historyRealtimeRows(recovered, vm.parameter).map(row => row.value), query.limit).filter(row => inLiveRange(Number(row.timestamp), range))
        const waiting = buffer; buffer = []
        waiting.filter(sample => !aggregate || sample.timestamp > to || sample.timestamp < from).forEach(apply)
        publish()
      } else { const waiting = buffer; buffer = []; waiting.forEach(apply) }
    } catch (error) { if (current() && !isRequestCanceled(error)) fail(error) }
    finally { correcting = false; if (current() && !halted) { if (recoveryPending) recover(); else scheduleCorrection() } }
  }
  if (stream.onReconnect) stream.onReconnect(recover)
  state.subscription = stream.subscribe(response => {
    if (!current() || halted) return
    const sample = shared ? measurementPropertySample(response, query) : propertySample(response && response.payload, query)
    if (!sample || !inLiveRange(sample.timestamp, range)) return
    try {
      if (!ready || correcting) {
        buffer.push(sample)
        if (buffer.length > 20000) throw new Error('初始化期间实时消息过多，请重试或缩小查询范围')
      } else apply(sample)
    } catch (error) { fail(error) }
  }, error => { streamError = error || new Error('实时订阅失败，请重试'); fail(streamError) },
  () => { streamError = new Error('实时订阅已结束，请重试回补历史'); fail(streamError) })
  if (state.subscription.ready) await state.subscription.ready
  if (!current()) return
  if (halted) { unsubscribeChart(state); throw streamError || new Error(state.error) }
  const response = aggregate
    ? await queryDevicePropertyAggregation(query.deviceId, query.data, options)
    : await queryDevicePropertyHistory(query.deviceId, query.property, query.data, options)
  if (!current()) return
  body = responseBody(response)
  if (aggregate) accumulator.seed(body)
  else rows = mergeRealtimeRows([], historyRealtimeRows(body, vm.parameter).map(row => row.value), query.limit)
    .filter(row => inLiveRange(Number(row.timestamp), range))
  ready = true
  const waiting = buffer; buffer = []
  waiting.forEach(apply)
  publish()
  if (streamError) throw streamError
  if (recoveryPending) recover()
}

export async function updateDeviceChart (vm) {
  stopDeviceChart(vm)
  const state = stateOf(vm), sequence = state.sequence, host = chartHost(vm), binding = bindingKey(vm)
  if (pendingChart(vm, host)) return
  // Vue watcher 尚未执行时也隔离旧设备/旧参数的返回。
  const current = () => !vm._isDestroyed && sequence === state.sequence && Number(vm.dataType) === 2 && binding === bindingKey(vm)
  state.status = 'loading'; state.error = ''
  renderData(vm, { categories: [], series: [] }, true)
  showState(vm)
  let query
  try {
    query = deviceChartQuery(vm.parameter, host.navigationContext || {}, !!host.menuFlag)
    if (isStaticDataPreview(host) && query.mode === 2) throw new Error('安全预览不建立实时订阅，请使用历史查询或正式查看')
    state.cancellation = createRequestCancellation()
    const options = sharedReadOptions(host, { cancelToken: state.cancellation.token })
    if (query.mode === 2) return await subscribeRealtime(vm, query, options, current)
    if (!isStaticDataPreview(host) && followsDeviceChart(vm.parameter)) return await subscribeHistory(vm, query, options, current)
    const response = query.mode === 0
      ? await queryDevicePropertyHistory(query.deviceId, query.property, query.data, options)
      : await queryDevicePropertyAggregation(query.deviceId, query.data, options)
    if (!current()) return
    acceptData(vm, transformDeviceChart(responseBody(response), vm.parameter, vm.dataFormatter, !!host.isPreview))
  } catch (error) {
    if (!current() || isRequestCanceled(error)) return
    if (query && followsDeviceChart(vm.parameter)) {
      clearTimeout(state.paintTimer); clearTimeout(state.correctionTimer)
      if (state.cancellation) state.cancellation.cancel('实时初始化失败')
      unsubscribeChart(state)
    }
    state.status = 'error'; state.error = error.message || '设备属性查询失败，请重试'; showState(vm)
  } finally {
    if (current() && query && !followsDeviceChart(vm.parameter) && !host.menuFlag && !host.isPreview) {
      const interval = Number(vm.parameter.refreshInterval === undefined ? vm.time : vm.parameter.refreshInterval)
      if (Number.isFinite(interval) && interval > 0) {
        // 完成后才安排下一次，避免慢请求重叠；相对区间在每次请求时重新计算。
        state.timer = setTimeout(() => updateDeviceChart(vm), Math.max(1000, interval))
      }
    }
  }
}

export function queueDeviceChart (vm) {
  stopDeviceChart(vm)
  const state = stateOf(vm)
  if (pendingChart(vm, chartHost(vm))) return
  state.status = 'loading'; state.error = ''
  // 切换页面设备时立即移除旧设备曲线；首值加载仅保留空轴，不遮挡组件。
  if (vm.parameter && vm.parameter.deviceSource === 'page') renderData(vm, { categories: [], series: [] }, true)
  showState(vm)
  state.timer = setTimeout(() => updateDeviceChart(vm), 100)
}

export function installDeviceChartRuntime (Vue) {
  if (Vue.__deviceChartRuntime) return
  Vue.__deviceChartRuntime = true
  Vue.mixin({
    beforeCreate () {
      if (!ownChart(this)) return
      const original = this.$options.methods.updateData
      // 在原实例上替换设备数据分支；动态接口、静态数据和其他图表仍调用原方法。
      this.$options.methods = { ...this.$options.methods, updateData (...args) {
        if (Number(this.dataType) === 2) return queueDeviceChart(this)
        stopDeviceChart(this)
        return original.apply(this, args)
      } }
    },
    created () {
      if (!ownChart(this)) return
      this.$watch(() => bindingKey(this), () => { this.updateData() })
    },
    mounted () { if (ownChart(this) && Number(this.dataType) === 2) queueDeviceChart(this) },
    beforeDestroy () { if (ownChart(this)) stopDeviceChart(this) }
  })
}
